import bindAll from 'lodash.bindall';
import defaultsDeep from 'lodash.defaultsdeep';
import PropTypes from 'prop-types';
import React from 'react';
import CustomProceduresComponent, {DISGUISED_CLICKING_TYPES} from '../components/custom-procedures/custom-procedures.jsx';
import {ScratchBlocks, ScratchExtensions} from '@hetao/scratch-core';
import {connect} from 'react-redux';
import DisguisedBlockConfigModal from "./disguised-block";
import {handleModalVisible, selectDisguisedBlock} from "../reducers/disguised-block";
import {getMode, isTeacherMode} from "../lib/hetao-mode";
import DropdownConfigModal from '../components/custom-procedures/dropdown.jsx';

const {DisguisedBlockly} = ScratchExtensions;
const DISGUISED_BLOCK_OPCODE_SUFFIX = DisguisedBlockly.DISGUISED_BLOCK_OPCODE_SUFFIX;

const teacherMode = isTeacherMode()

class CustomProcedures extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleAddLabel',
            'handleAddBoolean',
            'handleAddTextNumber',
            'handleAddDropdown',
            'handleToggleWarp',
            'handleCancel',
            'handleOk',
            'setBlocks',
            'handleSetCategory',
            'handleOpeningDisguisedBlockModal',
            'handleChangeDropdownArgs',
            'createEditOptionsButton',
        ]);
        this.state = {
            rtlOffset: 0,
            warp: false,
            category: '',
            options: [],
            dropdownConfigVisible: false,
            // 当前编辑的下拉框参数的索引
            currentEditorIdx: 0
        };
    }

    componentDidMount() {
        // 检测当前的自定义积木块有无关联的伪装块，如果有的话，设置selectDisguisedBlock
        const proccode = this.props.mutator.getAttribute('proccode')
        const disguisedBlock = this.props.vm.disguisedBlockEngine.findDisguisedBlockByProccode(proccode)
        if (disguisedBlock) {
            this.props.handleSelectDisguisedBlock(disguisedBlock.originalOpcode)
        }
        this.initialProccode = proccode;
    }

    componentWillUnmount () {
        if (this.workspace) {
            this.workspace.dispose();
        }
    }
    setBlocks (blocksRef) {
        if (!blocksRef) return;
        this.blocks = blocksRef;
        const workspaceConfig = defaultsDeep({},
            CustomProcedures.defaultOptions,
            this.props.options,
            {rtl: this.props.isRtl}
        );

        // @todo This is a hack to make there be no toolbox.
        const oldDefaultToolbox = ScratchBlocks.Blocks.defaultToolbox;
        ScratchBlocks.Blocks.defaultToolbox = null;
        this.workspace = ScratchBlocks.inject(this.blocks, workspaceConfig);
        ScratchBlocks.Blocks.defaultToolbox = oldDefaultToolbox;

        // Create the procedure declaration block for editing the mutation.
        this.mutationRoot = this.workspace.newBlock('procedures_declaration');
        // Make the declaration immovable, undeletable and have no context menu
        this.mutationRoot.setMovable(false);
        this.mutationRoot.setDeletable(false);
        this.mutationRoot.contextMenu = false;

        this.workspace.addChangeListener((e) => {
            // 监听输入框 focus 事件，如果是下拉框的话，就创建编辑按钮
            if (e.type == Blockly.Events.UI && e.element == 'showEditor') {
                const blockIdx = this.workspace.topBlocks_[0].childBlocks_.findIndex(block => block.id === e.blockId);
                const block = this.workspace.topBlocks_[0].childBlocks_[blockIdx];
                if (getMode() === 'TEACHER' && block && block.type === 'argument_editor_dropdown') {
                    this.createEditOptionsButton(blockIdx);
                }
                return;
            }

            this.mutationRoot.onChangeFn();
            // Keep the block centered on the workspace
            const metrics = this.workspace.getMetrics();
            const {x, y} = this.mutationRoot.getRelativeToSurfaceXY();
            const dy = (metrics.viewHeight / 2) - (this.mutationRoot.height / 2) - y;
            let dx;
            if (this.props.isRtl) {
                // // TODO: https://github.com/LLK/scratch-gui/issues/2838
                // This is temporary until we can figure out what's going on width
                // block positioning on the workspace for RTL.
                // Workspace is always origin top-left, with x increasing to the right
                // Calculate initial starting offset and save it, every other move
                // has to take the original offset into account.
                // Calculate a new left postion based on new width
                // Convert current x position into LTR (mirror) x position (uses original offset)
                // Use the difference between ltrX and mirrorX as the amount to move
                const ltrX = ((metrics.viewWidth / 2) - (this.mutationRoot.width / 2) + 25);
                const mirrorX = x - ((x - this.state.rtlOffset) * 2);
                if (mirrorX === ltrX) {
                    return;
                }
                dx = mirrorX - ltrX;
                const midPoint = metrics.viewWidth / 2;
                if (x === 0) {
                    // if it's the first time positioning, it should always move right
                    if (this.mutationRoot.width < midPoint) {
                        dx = ltrX;
                    } else if (this.mutationRoot.width < metrics.viewWidth) {
                        dx = midPoint - ((metrics.viewWidth - this.mutationRoot.width) / 2);
                    } else {
                        dx = midPoint + (this.mutationRoot.width - metrics.viewWidth);
                    }
                    this.mutationRoot.moveBy(dx, dy);
                    this.setState({rtlOffset: this.mutationRoot.getRelativeToSurfaceXY().x});
                    return;
                }
                if (this.mutationRoot.width > metrics.viewWidth) {
                    dx = dx + this.mutationRoot.width - metrics.viewWidth;
                }
            } else {
                dx = (metrics.viewWidth / 2) - (this.mutationRoot.width / 2) - x;
                // If the procedure declaration is wider than the view width,
                // keep the right-hand side of the procedure in view.
                if (this.mutationRoot.width > metrics.viewWidth) {
                    dx = metrics.viewWidth - this.mutationRoot.width - x;
                }
            }
            this.mutationRoot.moveBy(dx, dy);
        });
        this.mutationRoot.domToMutation(this.props.mutator);
        this.mutationRoot.initSvg();
        this.mutationRoot.render();
        this.setState({
            warp: this.mutationRoot.getWarp(),
            category: this.mutationRoot.getCategory(),
            options: this.mutationRoot.getOptions()
        });
        // Allow the initial events to run to position this block, then focus.
        setTimeout(() => {
            this.mutationRoot.focusLastEditor_();
        });
    }

    createEditOptionsButton(argIdx) {
        const div = Blockly.WidgetDiv.DIV;
        const editButton = document.createElement('button');
        editButton.textContent = '编辑选项';
        editButton.className = 'blocklyTextRemoveIcon';
        editButton.style.left = 'calc(50% + 20px)';
        editButton.style.width = 'max-content';
        editButton.style.marginLeft = '0';
        editButton.addEventListener('click', () => {
            this.setState({
                dropdownConfigVisible: true,
                currentEditorIdx: argIdx
            });
         })
        div.appendChild(editButton);
    }

    handleCancel () {
        this.props.onRequestClose();
        this.props.handleSelectDisguisedBlock(null)
    }
    handleOk () {
        const newMutation = this.mutationRoot ? this.mutationRoot.mutationToDom(true) : null;
        const originalProccode = newMutation.getAttribute('proccode');
        const proccode = originalProccode.split('%')[0].trim();
        if (ScratchBlocks._variableNameIgnoreRegex &&
            ScratchBlocks._variableNameIgnoreRegex.test(proccode)) {
            ScratchBlocks.alert(`你不能创建名称为${proccode}的自定义积木`);
            return false;
        }
        this.props.onRequestClose(newMutation);
        // process the disguised block
        const {selectedDisguisedBlock} = this.props;
        if (!proccode) {
            return;
        }
        const disguisedBlockEngine = this.props.vm.disguisedBlockEngine;
        // 没有选择伪装块保存，有可能是删除伪装代码块的关联，或者没有选择关联代码块
        const correspondingDisguisedBlock = disguisedBlockEngine.findDisguisedBlockByProccode(originalProccode)
        // 如果之前是有关联的代码块，编辑之后取消了选中，认为是取消了关联
        if (correspondingDisguisedBlock) {
            disguisedBlockEngine.removeDisguisedBlock(correspondingDisguisedBlock.originalOpcode)
        }
        // 之前定义过这个块，但之后编辑了mutator
        const initialDisguisedBlock = disguisedBlockEngine.findDisguisedBlockByProccode(this.initialProccode);
        if (this.initialProccode !== originalProccode && initialDisguisedBlock) {
            disguisedBlockEngine.removeDisguisedBlock(initialDisguisedBlock.originalOpcode)
        }
        if (selectedDisguisedBlock) {
            // 关联procedures块到内置块，也就是根据这个内置块的initJSON新增一个实际调用procedures的块。(包括传递参数)
            disguisedBlockEngine.linkProceduresToDisguisedBlock(selectedDisguisedBlock, originalProccode)
        }
        this.props.handleSelectDisguisedBlock(null)
    }
    handleAddLabel () {
        if (this.mutationRoot) {
            this.mutationRoot.addLabelExternal();
        }
    }
    handleAddBoolean () {
        if (this.mutationRoot) {
            this.mutationRoot.addBooleanExternal();
        }
    }
    handleAddTextNumber () {
        if (this.mutationRoot) {
            this.mutationRoot.addStringNumberExternal();
        }
    }
    handleAddDropdown () {
        if (this.mutationRoot) {
            this.mutationRoot.addDropdownExternal();
        }
    }
    handleToggleWarp () {
        if (this.mutationRoot) {
            const newWarp = !this.mutationRoot.getWarp();
            this.mutationRoot.setWarp(newWarp);
            this.setState({warp: newWarp});
        }
    }
    handleSetCategory (category) {
        if (this.mutationRoot) {
            this.mutationRoot.setCategory(category);
            this.setState({ category: category })
        }
    }

    handleChangeDropdownArgs (options) {
        const _options = [...this.state.options];
        _options[this.state.currentEditorIdx] = options;
        if (this.mutationRoot) {
            this.mutationRoot.setOptions(_options);
            this.setState({ options: _options })
        }
    }

    /**
     * @description opening a modal for allocating disguised block into real block.
     * @param openingModalType {DISGUISED_CLICKING_TYPES}
     * @return undefined
     */
    handleOpeningDisguisedBlockModal (openingModalType) {
        // 如果是修改就检测当前已经设置好的
        if (openingModalType === DISGUISED_CLICKING_TYPES.CHANGE) {
            // repeating usage detection in coding area
            const engine =  this.props.vm.disguisedBlockEngine;
            const selectedDisguisedBlock = this.props.selectedDisguisedBlock;
            const fakeBlockOpcode = `${selectedDisguisedBlock}_${DISGUISED_BLOCK_OPCODE_SUFFIX}`
            let repeatingUsage = engine.detectCertainBlockUsageInCodingArea(fakeBlockOpcode);
            if (repeatingUsage) {
                alert(`请删除代码区中的伪装代码块后再修改(${fakeBlockOpcode})`)
                return
            }
        }
        this.props.handleDisguisedConfigModalVisible(true)
    }


    render () {
        return (
            <React.Fragment>
                <DisguisedBlockConfigModal vm={this.props.vm} />
                <DropdownConfigModal
                    visible={this.state.dropdownConfigVisible}
                    options={this.state.options[this.state.currentEditorIdx] || []}
                    onClose={(options) => {
                        this.handleChangeDropdownArgs(options);
                        this.setState({dropdownConfigVisible: false});
                    }}
                />
                <CustomProceduresComponent
                    componentRef={this.setBlocks}
                    warp={this.state.warp}
                    category={this.state.category}
                    onAddBoolean={this.handleAddBoolean}
                    onAddLabel={this.handleAddLabel}
                    onAddTextNumber={this.handleAddTextNumber}
                    onAddDropdown={this.handleAddDropdown}
                    onCancel={this.handleCancel}
                    onOk={this.handleOk}
                    onToggleWarp={this.handleToggleWarp}
                    onSetCategory={this.handleSetCategory}
                    // disguised block about
                    disguisedBlockConfigurable={teacherMode}
                    selectedDisguisedBlock={this.props.selectedDisguisedBlock}
                    onOpenDisguiseBlockModal={this.handleOpeningDisguisedBlockModal}
                />
                
            </React.Fragment>
        );
    }
}

CustomProcedures.propTypes = {
    isRtl: PropTypes.bool,
    mutator: PropTypes.instanceOf(Element),
    onRequestClose: PropTypes.func.isRequired,
    options: PropTypes.shape({
        media: PropTypes.string,
        zoom: PropTypes.shape({
            controls: PropTypes.bool,
            wheel: PropTypes.bool,
            startScale: PropTypes.number
        }),
        comments: PropTypes.bool,
        collapse: PropTypes.bool
    })
};

CustomProcedures.defaultOptions = {
    zoom: {
        controls: false,
        wheel: false,
        startScale: 0.9
    },
    comments: false,
    collapse: false,
    scrollbars: true
};

CustomProcedures.defaultProps = {
    options: CustomProcedures.defaultOptions
};

const mapStateToProps = state => ({
    isRtl: state.locales.isRtl,
    mutator: state.scratchGui.customProcedures.mutator,
    selectedDisguisedBlock: state.scratchGui.disguisedBlock.selectedDisguisedBlock
});

const mapDispatchToProps = dispatch => ({
    handleSelectDisguisedBlock: disguisedBlock => dispatch(selectDisguisedBlock(disguisedBlock)),
    handleDisguisedConfigModalVisible: visible => dispatch(handleModalVisible(visible)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(CustomProcedures);
